
import { useEffect } from 'react';
import Header from '../../../../components/feature/Header';
import Footer from '../../../../components/feature/Footer';
import Button from '../../../../components/base/Button';

export default function RevenueOperationsFrameworkPage() {
  useEffect(() => {
    document.title = 'The Revenue Operations Framework That Scales Service Businesses | SparkBoost Labs';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Implement the systematic approach to revenue operations that aligns sales, marketing, and operations for sustainable growth.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Article Header */}
        <section className="py-20 bg-gradient-to-br from-[#1B365D] to-[#2BB6A3]">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="mb-6">
                <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Revenue Operations
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                The Revenue Operations Framework That Scales Service Businesses
              </h1>
              <div className="flex items-center justify-center text-white/80 text-sm mb-8">
                <span>January 3, 2024</span>
                <span className="mx-3">•</span>
                <span>10 min read</span>
                <span className="mx-3">•</span>
                <span>By James Hansen</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <img
                src="https://public.readdy.ai/ai/img_res/e99bbf35f7faeced49ae5114e76b7231.jpg"
                alt="Revenue Operations Framework"
                className="w-full h-64 md:h-96 object-cover object-top rounded-lg mb-12"
              />

              <div className="prose prose-lg max-w-none">
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Most service businesses operate with disconnected departments that work against each other instead of toward common revenue goals. Revenue Operations (RevOps) is the strategic alignment of sales, marketing, and customer success to drive predictable, scalable growth.
                </p>

                <h2 className="text-3xl font-bold text-[#1B365D] mb-6 mt-12">The Problem with Siloed Operations</h2>
                
                <p className="mb-6">
                  In traditional service businesses, each department operates independently with different goals, metrics, and systems:
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                    <h4 className="font-bold text-red-800 mb-3">Marketing</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Focuses on lead quantity</li>
                      <li>• Measures website traffic</li>
                      <li>• Optimizes for cost per lead</li>
                      <li>• Limited sales feedback</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                    <h4 className="font-bold text-red-800 mb-3">Sales</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Complains about lead quality</li>
                      <li>• Focuses on closing deals</li>
                      <li>• Limited customer feedback</li>
                      <li>• Inconsistent follow-up</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                    <h4 className="font-bold text-red-800 mb-3">Operations</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Focuses on delivery</li>
                      <li>• Limited sales input</li>
                      <li>• Reactive to problems</li>
                      <li>• No growth feedback loop</li>
                    </ul>
                  </div>
                </div>

                <p className="mb-8">
                  This siloed approach creates friction, inefficiency, and missed opportunities. Revenue Operations eliminates these silos by creating a unified system focused on revenue growth.
                </p>

                <h2 className="text-3xl font-bold text-[#1B365D] mb-6 mt-12">The SparkBoost Labs RevOps Framework</h2>

                <p className="mb-6">
                  Our Revenue Operations framework has helped service businesses achieve 300% revenue growth by aligning all revenue-generating activities. Here are the five core pillars:
                </p>

                <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Pillar 1: Unified Data and Systems</h3>
                <p className="mb-6">
                  The foundation of effective RevOps is a single source of truth for all customer data. This means:
                </p>

                <ul className="list-disc pl-6 mb-8 space-y-2">
                  <li><strong>Integrated CRM system:</strong> All customer interactions tracked in one place</li>
                  <li><strong>Marketing automation:</strong> Lead scoring and nurturing aligned with sales process</li>
                  <li><strong>Analytics platform:</strong> Real-time visibility into the entire revenue funnel</li>
                  <li><strong>Communication tools:</strong> Seamless handoffs between departments</li>
                </ul>

                <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Pillar 2: Aligned Metrics and Goals</h3>
                <p className="mb-6">
                  Instead of department-specific metrics, RevOps focuses on shared revenue metrics:
                </p>

                <div className="bg-gray-50 p-8 rounded-lg mb-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-3">Leading Indicators</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Marketing Qualified Leads (MQLs)</li>
                        <li>• Sales Qualified Leads (SQLs)</li>
                        <li>• Pipeline velocity</li>
                        <li>• Lead-to-customer conversion rate</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-3">Lagging Indicators</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Monthly Recurring Revenue (MRR)</li>
                        <li>• Customer Lifetime Value (CLV)</li>
                        <li>• Customer Acquisition Cost (CAC)</li>
                        <li>• Net Revenue Retention</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Pillar 3: Standardized Processes</h3>
                <p className="mb-6">
                  RevOps creates standardized, repeatable processes across the entire customer journey:
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#2BB6A3] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-2">Lead Generation Process</h4>
                      <p className="text-sm text-gray-600">Standardized lead qualification criteria and handoff procedures</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#2BB6A3] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-2">Sales Process</h4>
                      <p className="text-sm text-gray-600">Defined stages, exit criteria, and required activities</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#2BB6A3] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-2">Onboarding Process</h4>
                      <p className="text-sm text-gray-600">Smooth transition from sales to delivery team</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-[#2BB6A3] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <h4 className="font-bold text-[#1B365D] mb-2">Customer Success Process</h4>
                      <p className="text-sm text-gray-600">Proactive account management and expansion opportunities</p>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Pillar 4: Cross-Functional Communication</h3>
                <p className="mb-6">
                  Regular communication ensures all teams stay aligned on revenue goals:
                </p>

                <ul className="list-disc pl-6 mb-8 space-y-2">
                  <li><strong>Weekly RevOps meetings:</strong> Review pipeline, discuss blockers, align on priorities</li>
                  <li><strong>Monthly business reviews:</strong> Analyze performance against goals and adjust strategy</li>
                  <li><strong>Quarterly planning sessions:</strong> Set goals and allocate resources</li>
                  <li><strong>Real-time dashboards:</strong> Shared visibility into key metrics</li>
                </ul>

                <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Pillar 5: Continuous Optimization</h3>
                <p className="mb-6">
                  RevOps is not a set-it-and-forget-it system. It requires continuous testing and optimization:
                </p>

                <ul className="list-disc pl-6 mb-8 space-y-2">
                  <li><strong>A/B testing:</strong> Continuously test messaging, processes, and tactics</li>
                  <li><strong>Performance analysis:</strong> Regular deep-dives into what's working and what's not</li>
                  <li><strong>Process refinement:</strong> Ongoing improvements to reduce friction and increase efficiency</li>
                  <li><strong>Technology optimization:</strong> Regular evaluation and upgrade of tools and systems</li>
                </ul>

                <h2 className="text-3xl font-bold text-[#1B365D] mb-6 mt-12">Case Study: Professional Services Firm Transformation</h2>

                <p className="mb-6">
                  A 50-person professional services firm was struggling with inconsistent growth and departmental conflicts. Here's how RevOps transformed their business:
                </p>

                <div className="bg-gray-50 p-8 rounded-lg mb-8">
                  <h4 className="text-xl font-bold text-[#1B365D] mb-4">The Challenge</h4>
                  <ul className="list-disc pl-6 space-y-2 mb-6">
                    <li>Marketing and sales blamed each other for poor results</li>
                    <li>No visibility into pipeline or forecasting accuracy</li>
                    <li>Inconsistent client onboarding experience</li>
                    <li>High client churn in first 90 days</li>
                    <li>Difficulty scaling beyond founder involvement</li>
                  </ul>

                  <h4 className="text-xl font-bold text-[#1B365D] mb-4">The RevOps Implementation</h4>
                  <ul className="list-disc pl-6 space-y-2 mb-6">
                    <li>Implemented unified CRM and marketing automation</li>
                    <li>Created shared revenue metrics and dashboards</li>
                    <li>Standardized lead qualification and handoff process</li>
                    <li>Developed structured onboarding program</li>
                    <li>Established weekly cross-functional meetings</li>
                  </ul>

                  <h4 className="text-xl font-bold text-[#1B365D] mb-4">The Results (12 months)</h4>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#2BB6A3]">300%</div>
                      <div className="text-sm">Revenue Growth</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#2BB6A3]">85%</div>
                      <div className="text-sm">Forecast Accuracy</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#2BB6A3]">60%</div>
                      <div className="text-sm">Faster Sales Cycle</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#2BB6A3]">40%</div>
                      <div className="text-sm">Higher Retention</div>
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-[#1B365D] mb-6 mt-12">Implementing RevOps in Your Business</h2>

                <p className="mb-6">
                  RevOps implementation should be phased to minimize disruption while maximizing impact:
                </p>

                <div className="space-y-6 mb-8">
                  <div className="border-l-4 border-[#2BB6A3] pl-6">
                    <h4 className="font-bold text-[#1B365D] mb-2">Phase 1: Foundation (Months 1-2)</h4>
                    <p className="text-sm text-gray-600">Audit current systems, define shared metrics, implement unified CRM</p>
                  </div>
                  <div className="border-l-4 border-[#2BB6A3] pl-6">
                    <h4 className="font-bold text-[#1B365D] mb-2">Phase 2: Process Alignment (Months 3-4)</h4>
                    <p className="text-sm text-gray-600">Standardize handoff procedures, create communication cadence</p>
                  </div>
                  <div className="border-l-4 border-[#2BB6A3] pl-6">
                    <h4 className="font-bold text-[#1B365D] mb-2">Phase 3: Optimization (Months 5-6)</h4>
                    <p className="text-sm text-gray-600">Implement automation, refine processes, establish testing framework</p>
                  </div>
                </div>

                <p className="mb-8">
                  Remember: RevOps is a journey, not a destination. The goal is continuous improvement in revenue efficiency and predictability.
                </p>

                <div className="bg-gradient-to-r from-[#1B365D] to-[#2BB6A3] p-8 rounded-lg text-white text-center">
                  <h3 className="text-2xl font-bold mb-4">Ready to Implement RevOps?</h3>
                  <p className="mb-6">
                    Get a comprehensive RevOps assessment and implementation roadmap for your business. 
                    We'll show you exactly how to align your teams for predictable revenue growth.
                  </p>
                  <Button href="/free-assessment" variant="secondary">
                    Get Your Free RevOps Assessment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
